import {
  changeSort,
  changeTen99Filters,
  clear1099s_V2,
  clearSort,
  EmptyTen99Filter,
  FilterEnum,
  getTen99s_V2,
  Ten99StatusFilter
} from 'Agent/1099s/ducks/1099_v2';
import {Ten99} from 'Agent/1099s/models/1099';
import {Button, CircularProgress, FormControl, MenuItem, Paper, Select} from '@material-ui/core';
import {FeatureConstants, getOrCreateFeatureFlag} from 'Core/ducks/featureFlag';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {toUndefined} from 'fp-ts/lib/Option';
import React, {useEffect,useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import FileDownloadButton, {FileExtension} from 'Shared/components/FileDownloadButton';
import Column from 'Shared/components/PagedView/Column';
import {withSelectFilterProperties, withStringFilterProperties} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {RequestMethod} from 'Shared/utilities/fetchHelpers';
import {withValueChanged} from 'Shared/utilities/withValueChanged';
import {fetchTaxYears} from 'Core/ducks/taxYears';
import './1099_v2.sass';

export const get1099FileNameV2 = (ten99: Ten99) => `1099_${ten99.accountNumber}_${ten99.taxYear}`;

export const getTen99DownloadUrlV2 = (ten99FeatureFlag: boolean, documentId: string, encryptedIdentifier: string) => {
  const documentUri = ten99FeatureFlag ? 'document' : `${documentId}/document`;
  return `/api/commissions/v1/taxStatements/${documentUri}${addFilterParameters({encryptedIdentifier})}`;
};

type Ten99sProps = {
  selectedAgency: AgencyFilters;
} & RouteComponentProps;

const Ten99sV2 = (props: Ten99sProps) => {
  const dispatch = useDispatch();
  useBreadcrumbs('1099s', props.match.url);
  const {selectedAgency} = props;
  const {ten99sFilter, sort, ten99s} = useSelector((state: ReducerState) => state.ten99_v2);
  const {Tax1099Form} = useSelector((state: ReducerState) => state.featureFlag);
  const {taxYears, latestTaxYear, isLoading: isTaxYearsLoading} = useSelector((state: ReducerState) => state.dashboardTaxYears);
  const agentNumbers = useSelector((state: ReducerState) => state.user.profile.currentAgentNumbers);

  const requestMethod = Tax1099Form ? RequestMethod.POST : RequestMethod.GET;
  const fileExtension = Tax1099Form ? FileExtension.PDF : FileExtension.TIFF;

  const taxForms: FilterEnum[] = [
    {
      name: '1099-MISC',
      value: '1',
    },
    {
      name: '1099-NEC',
      value: '2',
    },
    {
      name: '1099-INT',
      value: '3',
    },
    {
      name: '1099-R',
      value: '4',
    },
  ];

  const taxYearFilters: FilterEnum[] = taxYears.map(taxYear => ({
    name: taxYear,
    value: taxYear,
  }));

  useEffect(() => {
    dispatch(getOrCreateFeatureFlag(FeatureConstants.TEN_99_PRO));
  }, []);

  useEffect(() => {
    dispatch(fetchTaxYears(
      selectedAgency.encryptedIdentifier,
      selectedAgency.isNewYork
    ));
  }, []);

  useEffect(() => {
    return () => {
      dispatch(clear1099s_V2());
      dispatch(changeTen99Filters(EmptyTen99Filter));
      dispatch(clearSort());
    };
  }, [clear1099s_V2, changeTen99Filters]);

  const changeFilter = withValueChanged(ten99sFilter, filter => dispatch(changeTen99Filters(filter)));

  const filterProperties = {
    changeFilter,
    filterData: ten99sFilter,
  };

  const appliedTaxYear = latestTaxYear || '';
  const [selectedTaxYear, setSelectedTaxYear] = useState(appliedTaxYear);
  const [activeTaxYear, setActiveTaxYear] = useState(appliedTaxYear);
  const isApplyFilterEnabled = selectedTaxYear !== activeTaxYear;
  const showNoData = !isTaxYearsLoading && !activeTaxYear;

  useEffect(() => {
    if (!activeTaxYear) {
      return;
    }
    dispatch(
      getTen99s_V2(
        agentNumbers,
        selectedAgency.encryptedIdentifier,
        selectedAgency.isNewYork,
        activeTaxYear,
        ten99sFilter,
        toUndefined(sort)
      )
    );
  }, [ten99sFilter, selectedAgency, getTen99s_V2, sort, activeTaxYear]);

  useEffect(() => {
    setSelectedTaxYear(appliedTaxYear);
    setActiveTaxYear(appliedTaxYear);
  }, [appliedTaxYear]);


  const handleTaxYearChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSelectedTaxYear(event.target.value as string);
  };

  const applyTaxFormTypeFilter = () => {
    const nextTaxYear = selectedTaxYear || appliedTaxYear;
    setActiveTaxYear(nextTaxYear);

    if (!nextTaxYear) {
      return;
    }

    dispatch(
      getTen99s_V2(
        agentNumbers,
        selectedAgency.encryptedIdentifier,
        selectedAgency.isNewYork,
        nextTaxYear,
        ten99sFilter,
        toUndefined(sort)
      )
    );
  };

  return (
     <>
    {isTaxYearsLoading && (
      <div className="ten99LoadingSpinner">
        <CircularProgress />
      </div>
    )}
    <div className="ten99Heading">
        1099 Forms
    </div>
    <Paper className="ten99FilterPanel">
      <div className="ten99FilterContent">
        <span>Filter By:</span>
        <FormControl variant="outlined" size="small" className="ten99TaxFormControl">
          <Select
            labelId="ten99-tax-year-label"
            id="ten99-tax-year"
            value={selectedTaxYear}
            onChange={handleTaxYearChange}
          >
            {taxYearFilters.map(taxYear => (
              <MenuItem key={taxYear.value} value={taxYear.value}>
                {taxYear.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={applyTaxFormTypeFilter}
          disabled={!isApplyFilterEnabled}
          className="ten99ApplyFilterButton"
        >
          Apply Filter
        </Button>
      </div>
    </Paper>
    <div className="ten99SubHeading">{selectedTaxYear} 1099 Forms</div>
    {showNoData ? (
      <div className="paged-table__content">No data found</div>
    ) : (
      <PagedView
        pagedViewKey="1099"
        loadMore={page => {
        if (!activeTaxYear) {
          return;
        }
        dispatch(
          getTen99s_V2(
            agentNumbers,
            selectedAgency.encryptedIdentifier,
            selectedAgency.isNewYork,
            activeTaxYear,
            ten99sFilter,
            toUndefined(sort),
            page
          )
        );
      }}
      data={ten99s}
      sort={toUndefined(sort)}
      onChangeSort={updatedSort => dispatch(changeSort(updatedSort))}
    >
      {columnProps => (
        <>
          <Column
            {...columnProps}
            title="Account Number"
            sortColumnKey="accountNumber"
            filterProperties={withStringFilterProperties(filterProperties, 'accountNumber')}
            default
            ddprivacy="mask"
          >
            {ten99 => ten99.accountNumber}
          </Column>
          <Column
            {...columnProps}
            title="Account Name"
            sortColumnKey="accountName"
            filterProperties={withStringFilterProperties(filterProperties, 'accountName')}
            default
            ddprivacy="mask"
          >
            {ten99 => ten99.accountName}
          </Column>
          <Column
            {...columnProps}
            title="Status"
            sortColumnKey="history"
            filterProperties={withSelectFilterProperties(filterProperties, 'history', Ten99StatusFilter)}
            default
          >
            {ten99 => ten99.history}
          </Column>
          <Column
            {...columnProps}
            title="Tax Year"
            sortColumnKey="taxYear"
            default
          >
            {ten99 => ten99.taxYear}
          </Column>
          <Column
            {...columnProps}
            title="Tax Form"
            sortColumnKey="taxFormType"
            filterProperties={withSelectFilterProperties(filterProperties, 'taxFormType', taxForms)}
            default
          >
            {ten99 => (Tax1099Form ? ten99.tax1099FormType?.name : ten99.taxFormType?.name)}
          </Column>

          <Column {...columnProps} title="" isActions default>
            {ten99 =>
              ten99.documentId ? (
                <FileDownloadButton
                  extension={fileExtension}
                  downloadedFileName={get1099FileNameV2(ten99)}
                  displayTooltip={false}
                  relativeFileLocation={getTen99DownloadUrlV2(
                    Tax1099Form,
                    ten99.documentId,
                    selectedAgency.encryptedIdentifier
                  )}
                  requestMethod={requestMethod}
                  requestPayload={{
                    documentId: ten99.documentId,
                    isTaxportSystem: ten99.isTaxportSystem,
                    partyTaxFormFChar: ten99.partyTaxFormFChar || null,
                    partyTaxFormSeqNbr: ten99.partyTaxFormSeqNbr || null,
                    taxYear: ten99.taxYear,
                    partyTaxFormId: ten99.partyTaxFormId || null,
                    partyTaxId: ten99.partyTaxId || null,
                  }}
                >
                  Download
                </FileDownloadButton>
              ) : (
                <div>N/A</div>
              )
            }
          </Column>
        </>
      )}
    </PagedView>
    )}
    </>
  );
};

export default Ten99sV2;